import { useIntl } from "react-intl";

// Components
import { Box, ListItemText, Typography, MenuItem, Popper, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import { RadioButton } from "components/RadioButton";

// Hooks
import { useAuth } from "utils/hooks";

// Constants
import { USERS_ADMIN_LIMIT } from "constants/constants";

// Icons
import icons from "enums/icons";
import CustomIcon from "components/CustomIcon";

// Styles
import userRoleSelectStyles from "./UserRoleSelect.styles";

// Ability
import { AbilityContext } from "context/Ability";
import { useAbility } from "@casl/react";

// Types
import { TUserRole } from "types/global";

const UserRoleSelect = ({
    data,
    list,
    onRoleSelection,
    adminsCount,
    disabled = false,
}: {
    data: any;
    list?: TUserRole[];
    onRoleSelection: (event: SelectChangeEvent, userId: number) => void;
    adminsCount: number;
    disabled?: boolean;
}) => {
    const intl = useIntl();
    const classes = userRoleSelectStyles();
    const { loggedInUserData } = useAuth();

    const ability = useAbility(AbilityContext);
    const canEditUserRole = ability.can("see", "SYS_ADMIN_USER_GROUP_ROLES_EDIT");
    const isLoggedInUser = loggedInUserData.userId === data.id;

    const wasAdminLimitExceded = adminsCount === USERS_ADMIN_LIMIT;

    const SelectItem = ({ role }: { role: TUserRole }) => {
        const isSelectedRole = role.id === data.function;
        const AdminCount = () => {
            return role.id === "admin" ? <Box ml={0.5}>({`${adminsCount}/${USERS_ADMIN_LIMIT}`})</Box> : <></>;
        };

        return (
            <Box display="flex" alignItems="center">
                <RadioButton checked={isSelectedRole} />
                <ListItemText
                    primary={
                        <Box display="flex">
                            {role.label} <AdminCount />
                        </Box>
                    }
                    secondary={role?.description}
                />
            </Box>
        );
    };

    return (
        <Select
            id={`role-${data.id}`}
            labelId={`role-${data.id}`}
            name="role-select"
            className={classes.selectField}
            onClick={(event) => event.stopPropagation()}
            value={data.function ?? ""}
            disabled={disabled || !canEditUserRole || isLoggedInUser}
            onChange={(event: SelectChangeEvent) => onRoleSelection(event, data.id)}
            renderValue={(selected) => {
                const role = list?.find((role) => role.id === selected);
                const isPlaceholder = selected === "placeholder";
                return (
                    <Typography
                        sx={(theme) => ({
                            color: isPlaceholder ? theme.palette.text.disabled : theme.palette.text.primary,
                        })}
                    >
                        {role?.label}
                    </Typography>
                );
            }}
            IconComponent={(props) => <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />}
            MenuProps={{
                PaperProps: {
                    className: classes.filterByRolePaper,
                },
            }}
        >
            {list
                ?.filter((role) => role.id !== "placeholder")
                .map((role) => {
                    const isDisabled = wasAdminLimitExceded && role.id === "admin";
                    return isDisabled ? (
                        <Tooltip
                            arrow
                            key={role.id}
                            enterDelay={500}
                            placement="bottom"
                            disableInteractive
                            disableHoverListener={!isDisabled}
                            disableFocusListener={!isDisabled}
                            title={intl.formatMessage({
                                id: "hubManagement.client.message.tooManyAdminsAssigned",
                            })}
                            PopperComponent={(props) => <Popper {...props} sx={{ top: `-18px !important` }} />}
                        >
                            <span key={role.id}>
                                <MenuItem value={role.id} disabled={isDisabled} className={classes.roleSelectMenuItem}>
                                    <SelectItem role={role} />
                                </MenuItem>
                            </span>
                        </Tooltip>
                    ) : (
                        <MenuItem key={role.id} value={role.id} className={classes.roleSelectMenuItem}>
                            <SelectItem role={role} />
                        </MenuItem>
                    );
                })}
        </Select>
    );
};

export default UserRoleSelect;
